import { useTranslation } from '@rhino-project/core/i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDate, formatNumber } from '@rhino-project/core/i18n/format';
import {
  useBaseOwnerId,
  useStripeEnabled,
  useModelIndex
} from '@rhino-project/core/hooks';
import {
  useCheckout,
  usePortal,
  useUpdatePlan
} from '@rhino-project/core/queries';
import { useLocation, useSearch } from '@tanstack/react-router';
import {
  Alert,
  AlertContent,
  AlertDialog,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CheckboxContent,
  CheckboxControl,
  CheckboxIndicator,
  Chip,
  Typography
} from '@heroui/react';

type PackageKind = 'standard' | 'base' | 'addon';

interface PackageResource {
  id: number;
  name: string;
  slug: string;
  kind?: PackageKind;
  features?: string[];
}
interface SubscriptionItemResource {
  id: number;
  package?: PackageResource | null;
  expires_at?: string | null;
}
interface SubscriptionResource {
  status?: string;
  current_period_end?: string | null;
  subscription_items?: SubscriptionItemResource[];
}

// Any 2+ plans get 10% off (Stripe coupons bundle_2_cad / bundle_3_cad); a lone
// plan is full price. Client-side preview only — Stripe applies the coupon at checkout.
const PLAN_PRICE_CAD = 1200;
const totalForCount = (n: number) =>
  n >= 2 ? Math.round(PLAN_PRICE_CAD * n * 0.9) : PLAN_PRICE_CAD * n;
const formatCad = (n: number) =>
  formatNumber(n, {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0
  });

// A feature slug (e.g. "custom_domains") is the only human-facing label the API
// exposes for a feature, so title-case it for display.
const humanize = (slug: string) =>
  slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const Subscription = () => {
  const { t } = useTranslation();
  const baseOwnerId = useBaseOwnerId();
  const stripeEnabled = useStripeEnabled();
  const { status } = useSearch({ strict: false }) as { status?: string };
  const { pathname } = useLocation();

  // Scope to the current org via the `base_owner` association. A framework model
  // owns through `base_owner` (it can't hardcode `organization`), so the generic
  // base-owner filter's model-name key ("organization") isn't a recognized
  // reflection and the backend would silently return every org's subscription.
  const { resources: subs, refetch: refetchSubscription } = useModelIndex(
    'rhino_subscription',
    {
      filter: { base_owner: baseOwnerId },
      queryOptions: { enabled: !!baseOwnerId }
    }
  );
  const subscription = (subs?.results?.[0] ??
    null) as SubscriptionResource | null;
  const items = subscription?.subscription_items ?? [];

  // A Stripe-backed subscription reports a status (offline/admin grants leave it
  // null). Once one exists, adding/removing packages modifies it in place;
  // cancellation still goes through the billing portal.
  const hasStripeSubscription =
    !!subscription?.status && subscription.status !== 'canceled';
  const currentPackageIds = useMemo(
    () =>
      (subscription?.subscription_items ?? [])
        .map((i) => i.package?.id)
        .filter((id): id is number => id != null),
    [subscription]
  );

  const { resources: pkgs } = useModelIndex('rhino_package', {
    queryOptions: { enabled: stripeEnabled }
  });
  const packages = useMemo(
    () => (pkgs?.results ?? []) as PackageResource[],
    [pkgs]
  );

  const bases = useMemo(
    () => packages.filter((p) => p.kind === 'base'),
    [packages]
  );
  const addons = useMemo(
    () => packages.filter((p) => p.kind === 'addon'),
    [packages]
  );
  const purchasablesInOrder = useMemo(
    () => [...bases, ...addons],
    [bases, addons]
  );
  const addonIds = useMemo(() => addons.map((a) => a.id), [addons]);

  const [selected, setSelected] = useState<number[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Seed the selection from the org's current packages once its subscription has
  // loaded (and re-seed on org switch), so an existing subscriber edits from
  // where their plan stands and a fresh subscriber starts from empty.
  const seededOrgRef = useRef<number | null>(null);
  useEffect(() => {
    if (!subs || seededOrgRef.current === baseOwnerId) return;
    seededOrgRef.current = baseOwnerId;
    setSelected(currentPackageIds);
  }, [subs, baseOwnerId, currentPackageIds]);
  const selectionChanged = useMemo(
    () =>
      selected.length !== currentPackageIds.length ||
      selected.some((id) => !currentPackageIds.includes(id)),
    [selected, currentPackageIds]
  );
  // Names added/removed relative to the current plan, for the confirm dialog.
  const planChange = useMemo(() => {
    const nameById = new Map(packages.map((p) => [p.id, p.name]));
    return {
      added: selected
        .filter((id) => !currentPackageIds.includes(id))
        .map((id) => nameById.get(id))
        .filter((n): n is string => !!n),
      removed: currentPackageIds
        .filter((id) => !selected.includes(id))
        .map((id) => nameById.get(id))
        .filter((n): n is string => !!n)
    };
  }, [packages, selected, currentPackageIds]);
  const hasBaseSelected = useMemo(
    () => bases.some((b) => selected.includes(b.id)),
    [bases, selected]
  );

  const toggleBase = useCallback(
    (id: number, on: boolean) =>
      setSelected((prev) => {
        let next = on ? [...prev, id] : prev.filter((x) => x !== id);
        // An add-on can't stand alone: dropping the last base clears add-ons.
        if (!bases.some((b) => next.includes(b.id))) {
          next = next.filter((x) => !addonIds.includes(x));
        }
        return next;
      }),
    [bases, addonIds]
  );

  const toggleAddon = useCallback(
    (id: number, on: boolean) =>
      setSelected((prev) =>
        on ? [...prev, id] : prev.filter((x) => x !== id)
      ),
    []
  );

  const checkout = useCheckout();
  const updatePlan = useUpdatePlan();
  const portal = usePortal();
  const returnPath = pathname;

  const handleSubmit = useCallback(() => {
    const packageIds = purchasablesInOrder
      .filter((p) => selected.includes(p.id))
      .map((p) => p.id);
    if (packageIds.length === 0) return;
    // An existing subscriber changes their plan in place, but only behind a
    // confirm — the change bills their card on file immediately. A new one goes
    // through hosted Checkout (which mints the subscription and takes payment).
    if (hasStripeSubscription) {
      setConfirmOpen(true);
    } else {
      checkout.mutate({
        base_owner_id: baseOwnerId,
        package_ids: packageIds,
        return_path: returnPath
      });
    }
  }, [
    checkout,
    hasStripeSubscription,
    baseOwnerId,
    returnPath,
    purchasablesInOrder,
    selected
  ]);

  const handleConfirmUpdate = useCallback(() => {
    const packageIds = purchasablesInOrder
      .filter((p) => selected.includes(p.id))
      .map((p) => p.id);
    if (packageIds.length === 0) return;
    updatePlan.mutate(
      { base_owner_id: baseOwnerId, package_ids: packageIds },
      {
        onSuccess: () => {
          setConfirmOpen(false);
          refetchSubscription();
        }
      }
    );
  }, [
    updatePlan,
    baseOwnerId,
    refetchSubscription,
    purchasablesInOrder,
    selected
  ]);

  const handleManage = useCallback(
    () =>
      portal.mutate({ base_owner_id: baseOwnerId, return_path: returnPath }),
    [portal, baseOwnerId, returnPath]
  );

  const periodEnd = subscription?.current_period_end
    ? formatDate(subscription.current_period_end)
    : null;
  const total = totalForCount(selected.length);

  const renderPackage = (pkg: PackageResource, gated: boolean) => {
    const checked = selected.includes(pkg.id);
    const disabled = gated && !checked && !hasBaseSelected;
    const onChange = (on: boolean) =>
      pkg.kind === 'addon' ? toggleAddon(pkg.id, on) : toggleBase(pkg.id, on);

    return (
      <Card key={pkg.id}>
        <CardHeader>
          <Checkbox
            isSelected={checked}
            isDisabled={disabled}
            onChange={onChange}
          >
            <CheckboxContent>
              <CheckboxControl>
                <CheckboxIndicator />
              </CheckboxControl>
              {pkg.name}
            </CheckboxContent>
          </Checkbox>
        </CardHeader>
        <CardContent>
          <div className="flex flex-row flex-wrap items-center gap-1">
            {(pkg.features ?? []).length > 0 && (
              <span className="text-xs text-muted">
                {t('rhino.frontend.messages.includes')}
              </span>
            )}
            {(pkg.features ?? []).map((slug) => (
              <Chip key={slug} size="sm" variant="soft">
                {humanize(slug)}
              </Chip>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-col gap-2">
        <Typography.Heading level={4} weight="medium">
          {t('rhino.frontend.messages.current_plan')}
        </Typography.Heading>
        {items.length > 0 ? (
          <div className="flex flex-col gap-2">
            {items.map((item) => (
              <Card key={item.id}>
                <CardContent className="flex flex-row items-start justify-between gap-4">
                  <div className="flex flex-col gap-1.5">
                    <span className="font-medium">{item.package?.name}</span>
                    {(item.package?.features ?? []).length > 0 && (
                      <div className="flex flex-row flex-wrap items-center gap-1">
                        <span className="text-xs text-muted">
                          {t('rhino.frontend.messages.includes')}
                        </span>
                        {(item.package?.features ?? []).map((slug) => (
                          <Chip key={slug} size="sm" variant="soft">
                            {humanize(slug)}
                          </Chip>
                        ))}
                      </div>
                    )}
                  </div>
                  {item.expires_at && (
                    <span className="whitespace-nowrap text-sm text-muted">
                      {t('rhino.frontend.messages.expires')}
                      {formatDate(item.expires_at)}
                    </span>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <span className="text-sm text-muted">
            {t('rhino.frontend.messages.no_active_plan')}
          </span>
        )}
        {periodEnd && (
          <span className="text-sm text-muted">
            {t('rhino.frontend.messages.renews_on_date', { date: periodEnd })}
          </span>
        )}
        {hasStripeSubscription && (
          <span className="text-sm text-muted">
            {t(
              'rhino.frontend.messages.cancel_your_plan_in_the_billing_portal'
            )}
          </span>
        )}
        {stripeEnabled && items.length > 0 && (
          <div>
            <Button
              size="sm"
              onPress={handleManage}
              isPending={portal.isPending}
            >
              {t('rhino.frontend.messages.manage_billing')}
            </Button>
          </div>
        )}
      </section>

      {status === 'success' && (
        <Alert status="success">
          <AlertContent>
            <AlertTitle>
              {t('rhino.frontend.messages.payment_completed_successfully')}
            </AlertTitle>
          </AlertContent>
        </Alert>
      )}
      {status === 'canceled' && (
        <Alert status="danger">
          <AlertContent>
            <AlertTitle>
              {t('rhino.frontend.messages.payment_was_canceled')}
            </AlertTitle>
          </AlertContent>
        </Alert>
      )}

      {stripeEnabled && (
        <section className="flex flex-col gap-3">
          <Typography.Heading level={4} weight="medium">
            {hasStripeSubscription
              ? t('rhino.frontend.messages.change_your_plan')
              : t('rhino.frontend.messages.choose_your_packages')}
          </Typography.Heading>

          {bases.length > 0 && (
            <div className="flex flex-col gap-2">
              <Typography.Heading level={5} weight="medium">
                {t('rhino.frontend.messages.plans')}
              </Typography.Heading>
              <div className="flex flex-row flex-wrap gap-2">
                {bases.map((pkg) => renderPackage(pkg, false))}
              </div>
            </div>
          )}

          {addons.length > 0 && (
            <div className="flex flex-col gap-2">
              <Typography.Heading level={5} weight="medium">
                {t('rhino.frontend.messages.add_ons')}
              </Typography.Heading>
              <div className="flex flex-row flex-wrap gap-2">
                {addons.map((pkg) => renderPackage(pkg, true))}
              </div>
            </div>
          )}

          <p data-testid="purchase-total" className="text-sm font-medium">
            {t('rhino.frontend.messages.total')}
            {formatCad(total)} {t('rhino.frontend.messages.cad_year')}
          </p>

          <div>
            <Button
              onPress={handleSubmit}
              isDisabled={
                selected.length === 0 ||
                (hasStripeSubscription && !selectionChanged)
              }
              isPending={checkout.isPending || updatePlan.isPending}
            >
              {hasStripeSubscription
                ? t('rhino.frontend.messages.update_plan')
                : t('rhino.frontend.messages.checkout')}
            </Button>
          </div>
        </section>
      )}

      <AlertDialog isOpen={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialog.Backdrop>
          <AlertDialog.Container size="sm">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>
                  {t('rhino.frontend.messages.confirm_plan_change')}
                </AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                <div className="flex flex-col gap-2 text-sm">
                  {planChange.added.length > 0 && (
                    <p>
                      <span className="font-medium">
                        {t('rhino.frontend.messages.adding')}
                      </span>{' '}
                      {planChange.added.join(', ')}
                    </p>
                  )}
                  {planChange.removed.length > 0 && (
                    <p>
                      <span className="font-medium">
                        {t('rhino.frontend.messages.removing_label')}
                      </span>{' '}
                      {planChange.removed.join(', ')}
                    </p>
                  )}
                  <p className="font-medium text-warning">
                    {t(
                      'rhino.frontend.messages.this_takes_effect_immediately_and_bills_your_card_on_file_for_the_prorated_difference'
                    )}
                  </p>
                  <p className="text-muted">
                    {t('rhino.frontend.messages.new_total')}
                    {formatCad(total)} {t('rhino.frontend.messages.cad_year')}
                  </p>
                </div>
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="ghost" onPress={() => setConfirmOpen(false)}>
                  {t('rhino.frontend.messages.cancel')}
                </Button>
                <Button
                  onPress={handleConfirmUpdate}
                  isPending={updatePlan.isPending}
                >
                  {t('rhino.frontend.messages.confirm_change')}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
};
